import { MapContainer, TileLayer, Polyline, Popup } from 'react-leaflet';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MapServiceTZ from '../../../services/MapServiceTZ';
import css from '../TReportCenter/Report.module.css';
import { MdLocalPolice } from 'react-icons/md';
import ShowBox from '../../../components/ShowBox';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { getYears } from '../../../interfaces/traffic_accidents/FormControllerData';

const segmentsCache = new Map<string, TZSegment[]>();

interface TZSegment {
  count: number;
  segment: [number, number][];
}

function getColor(count: number): string {
  if (count >= 10) return '#212121';
  if (count >= 6)  return '#c62828';
  return '#f57c00';
}

function getWeight(count: number): number {
  if (count >= 10) return 10;
  if (count >= 6)  return 8;
  return 6;
}

export default function TZRiskSectionsMap() {
  const { t } = useTranslation('traffic');

  const [segments, setSegments] = useState<TZSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [minCountInput, setMinCountInput] = useState<number>(3);
  const [minCount, setMinCount] = useState<number>(3);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [visibleLevels, setVisibleLevels] = useState({ low: true, medium: true, high: true });
  const years = useMemo(() => getYears(), []);

  useEffect(() => {
    const cacheKey = `${minCount}_${selectedYear}`;
    const cached = segmentsCache.get(cacheKey);
    if (cached) {
      setSegments(cached);
      setLoading(false);
      return;
    }
    setLoading(true);
    MapServiceTZ.getSegments(minCount, selectedYear)
      .then((resp: any) => {
        segmentsCache.set(cacheKey, resp.data);
        setSegments(resp.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [minCount, selectedYear]);

  useEffect(() => {
    const timer = setTimeout(() => setMinCount(minCountInput), 300);
    return () => clearTimeout(timer);
  }, [minCountInput]);

  const computedSegments = useMemo(() =>
    segments.map((s, idx) => ({
      key:     `c${idx}_${s.count}`,
      count:   s.count,
      segment: s.segment,
      color:   getColor(s.count),
      weight:  getWeight(s.count),
    })),
  [segments]);

  const handleMinCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    setMinCountInput(isNaN(val) || val < 3 ? 3 : val);
  };

  return (
    <>
      {/* Header */}
      <div style={{ marginTop: '30px', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '26px' }}>
          <strong>{t('Map.riskovyUsekMapaNadpis')}</strong>
        </h1>
        <p style={{ color: '#555', fontSize: '14px', marginBottom: '20px' }}>
          {t('Map.riskovyUsekNadpisDesc')}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontWeight: 600, fontSize: '14px' }}>
              {t('Map.minPocetNehod')}
            </label>
            <input
              type="number"
              min={3}
              value={minCountInput}
              onChange={handleMinCountChange}
              style={{ width: '80px', padding: '6px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px' }}
            />
          </div>
          <FormControl size="small" style={{ minWidth: 120 }}>
            <InputLabel id="tz-year-label">{t('Map.rok')}</InputLabel>
            <Select
              labelId="tz-year-label"
              value={selectedYear}
              label={t('Map.rok')}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <MenuItem value=""><em>{t('Map.vsetkyRoky')}</em></MenuItem>
              {years.map((y) => (
                <MenuItem key={y.Code} value={y.Code}>{y.Type}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'inline-block', backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '6px', padding: '6px 14px', fontSize: '13px', color: '#1565c0' }}>
            {t('Map.zobrazeneUseky', { count: computedSegments.length })}
          </div>
        </div>
      </div>

      {/* Map */}
      <ShowBox className="box-12 py-2" name="">
        <div style={{ position: 'relative' }}>
          {(loading || minCountInput !== minCount) && (
            <div className={css.loading}>
              <div><MdLocalPolice /> {t('Traffic.loading')}</div>
            </div>
          )}
          <MapContainer
          center={[49.8, 15.5]}
          zoom={7.5}
          style={{ zIndex: 1, width: '100%', height: '600px' }}
          maxZoom={18}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {computedSegments
            .filter(seg =>
              seg.count >= 10 ? visibleLevels.high
              : seg.count >= 6 ? visibleLevels.medium
              : visibleLevels.low
            )
            .map((seg) => (
              <>
                {/* white halo around segment */}
                <Polyline
                  key={seg.key + '_halo'}
                  positions={seg.segment}
                  pathOptions={{ color: 'white', weight: seg.weight + 5, opacity: 0.65, lineCap: 'round', lineJoin: 'round' }}
                />
                <Polyline
                  key={seg.key}
                  positions={seg.segment}
                  pathOptions={{ color: seg.color, weight: seg.weight, opacity: 1, lineCap: 'round', lineJoin: 'round' }}
                >
                  <Popup>
                    <strong>{t('Map.pocetNehodVUseku', { count: seg.count })}</strong>
                  </Popup>
                </Polyline>
              </>
            ))}
        </MapContainer>
        </div>
      </ShowBox>

      {/* Legend */}
      <div style={{ marginTop: '24px', marginBottom: '32px' }}>
        <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>
          <strong>{t('Map.legendaRizika')}</strong>
        </h3>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {([
            { color: '#f57c00', label: t('Map.rizikoNizke'),   level: 'low'    as const },
            { color: '#c62828', label: t('Map.rizikoStredne'), level: 'medium' as const },
            { color: '#212121', label: t('Map.rizikoVysoke'),  level: 'high'   as const },
          ]).map((item) => (
            <label key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={visibleLevels[item.level]}
                onChange={() => setVisibleLevels(prev => ({ ...prev, [item.level]: !prev[item.level] }))}
              />
              <div style={{ width: '32px', height: '6px', borderRadius: '3px', backgroundColor: item.color, opacity: visibleLevels[item.level] ? 1 : 0.35 }} />
              {item.label}
            </label>
          ))}
        </div>
      </div>
    </>
  );
}